import { t } from 'i18next';

import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { GlobalHelper } from '../utils/GlobalHelper';
import { ScreenshotHelper } from '../utils/ScreenshotHelper';
import { ArticleItem } from '../stores/ArticleStore';
import { Stats } from '../utils/Stats';
import { ViewSettingsActions } from './ViewSettingsActions';
import { EAIWSStoreSyncState, EAIWSStore } from '../stores/EAIWSStore';
import { VisibilityActions } from './VisibilityActions';

import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;

export class PropertyEditorActions {
    private static sArticleImageOptions: cf.ArticleScreenshotOptions = {
        width: 48 * 2,
        height: 48 * 2,
        margin: 0,
        hideSubArticles: false
    };

    public static setup(pProperties: Map<string, Array<core.prop.Property>>, pPropertyClasses: Array<core.prop.PropertyClass>): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.PE_SETUP,
            value: {properties: pProperties, classes: pPropertyClasses}
        });
    }

    public static invalidateProperties(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.PE_INVALIDE
        });
    }

    public static async updateProperties(): Promise<void> {
        try {
            const properties: Array<core.prop.Property> = await GlobalHelper.coreApp.model.selectionProperties.getProperties();
            const propClasses: Array<core.prop.PropertyClass> = await GlobalHelper.coreApp.model.selectionProperties.getPropertyClasses();
            PropertyEditorActions.buildPropertyList(propClasses, properties);
        } catch (_) {
            PropertyEditorActions.buildPropertyList([], []);
        }
    }

    public static async getArticleItem(): Promise<void> {
        if (GlobalHelper.coreApp.model.selection.length === 1) {
            const selectedMainArticle: cf.ArticleElement = GlobalHelper.coreApp.model.selection[0] as cf.ArticleElement; // FixMe
            const selectedSubArticle: cf.SubArticleElement = GlobalHelper.coreApp.model.getSubElementSelection(selectedMainArticle) as cf.SubArticleElement;
            const sourceArticle: cf.ArticleElement = selectedSubArticle != null ? selectedSubArticle : selectedMainArticle;
            const data: string | null = await ScreenshotHelper.getArticleImage(
                sourceArticle,
                PropertyEditorActions.sArticleImageOptions
            );
            const properties: eaiws.basket.ItemProperties = await sourceArticle.getItemProperties();
            AppDispatcher.dispatch({
                actionType: ActionConstants.PE_ARTICLE_ITEM,
                value: {
                    article: sourceArticle,
                    properties: properties,
                    image: data,
                } as ArticleItem
            });
        }
    }

    public static onChoiceClick(property: core.prop.Property, choiceValue: core.prop.PropertyValueType): void {
        PropertyEditorActions.setPropertyValue(property, choiceValue);
    }

    public static async setPropertyValue(property: core.prop.Property, value: core.prop.PropertyValueType): Promise<core.prop.PropertyChangedResult> {
        Stats.sendEvent({
            category: 'Article',
            action: 'configure',
            label: 'settings'
        });

        return await ViewSettingsActions.withLock<core.prop.PropertyChangedResult>((): Promise<core.prop.PropertyChangedResult> => {
            return GlobalHelper.coreApp.model.selectionProperties.setPropertyValueUndoable(property, value);
        });
    }

    public static onPropertyClick(property: core.prop.Property | null): void {
        if (!property || !property.editable) {
            AppDispatcher.dispatch({
                actionType: ActionConstants.PE_SETACTIVE,
                value: {property: null, choiceList: null}
            });
            return;
        }

        if (property.choiceList) {
            property.getChoices().then((pChoiceList) => {
                if (pChoiceList != null && pChoiceList.length > 0) {
                    AppDispatcher.dispatch({
                        actionType: ActionConstants.PE_SETACTIVE,
                        value: {property: property, choiceList: pChoiceList}
                    });
                }
            });
        } else if (property.type === core.prop.PropertyType.Length) {
            const propertyValue: core.prop.PropertyValue | null = property.getValue();
            if (propertyValue != null && utils.number.isNumber(propertyValue.value)) {
                VisibilityActions.setDialog({
                    callback: (value: string): void => {PropertyEditorActions.setPropertyValue(property, GlobalHelper.getValueFromLocaleLengthString(value)); },
                    label: property.getName(),
                    default: GlobalHelper.getLocaleLengthString(propertyValue.value),
                    type: 'text'
                });
            }
        } else {
            VisibilityActions.setDialog({
                callback: (value: core.prop.PropertyValueType): void => {PropertyEditorActions.setPropertyValue(property, value); },
                label: property.getName(),
                default: property.getValueAsString(),
                type: 'text'
            });
        }
    }

    public static async syncEAIWSState(): Promise<void> {
        const syncState: EAIWSStoreSyncState = EAIWSStore.getState().syncState;
        if (!EAIWSStoreSyncState.syncInProgress(syncState) && EAIWSStoreSyncState.shouldSync(syncState)) {
            try {
                AppDispatcher.dispatch({
                    actionType: ActionConstants.PE_START_SYNC
                });

                return await ViewSettingsActions.withSidebarLock<void>((): Promise<void> => {
                    return Promise.all<void>([
                        PropertyEditorActions.updateProperties(),
                        GlobalHelper.coreApp.model.materials.waitUntilTexturesLoaded().then((): Promise<void> => {
                            return PropertyEditorActions.getArticleItem();
                        })
                    ]).then(() => { return; });
                });
            } finally {
                AppDispatcher.dispatch({
                    actionType: ActionConstants.PE_STOP_SYNC
                });
            }
        }

        return Promise.resolve();
    }

    protected static buildPropertyList(
        pPropertyClasses: Array<core.prop.PropertyClass>,
        pProperties: Array<core.prop.Property>
    ): void {
        if (pProperties == null) {
            return;
        }
        const tResultPropertyClasses: Array<core.prop.PropertyClass> = pPropertyClasses != null ? utils.array.clone(pPropertyClasses) : new Array();
        // add default property class
        const tDefaultClass: core.prop.PropertyClass = new core.prop.PropertyClass();
        tDefaultClass.key = '';
        tDefaultClass.name = t('Properties');
        tResultPropertyClasses.push(tDefaultClass);
        const tResultProperties: Map<string, Array<core.prop.Property>> = new Map();
        for (const tProp of pProperties) {
            if (tProp.visible) {
                let tClass: string | null = tProp.class;
                if (tClass == null) {
                    tClass = '';
                }

                let tProperties: Array<core.prop.Property> | undefined = tResultProperties.get(tClass);
                if (tProperties == null) {
                    tProperties = new Array<core.prop.Property>();
                    tResultProperties.set(tClass, tProperties);
                }
                tProperties.push(tProp);
            }
        }
        PropertyEditorActions.setup(tResultProperties, tResultPropertyClasses);
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/PropertyEditorActions.ts